import React, { useMemo, useCallback } from 'react';
import {
  DataGrid,
  Column,
  RenderCellProps,
  RowsChangeData
} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { Employee } from '../../types';
import NotesCell from './NotesCell';
import PositionCell from './PositionCell';
import { HEADER_ROW_HEIGHT, POSITION_OPTIONS } from '../../constants/appConstants';

interface EmployeeGridProps {
  employees: Employee[];
  rowHeightGetter: (row: Employee) => number;
  onRowsChange: (rows: Employee[], data: RowsChangeData<Employee>) => void;
  onRowClick: (employee: Employee) => void;
  onDeleteRow: (id: number) => void;
  onEditRow: (employee: Employee) => void;
  onNotesChange: (id: number, value: string) => void;
  onNotesBlur: (employee: Employee) => void;
  syncRowHeight: (id: number, textareaHeight: number) => void;
  onPositionChange?: (id: number, position: string) => void;
  isAdmin?: boolean;
}

const EmployeeGrid: React.FC<EmployeeGridProps> = ({
  employees,
  rowHeightGetter,
  onRowsChange,
  onRowClick,
  onDeleteRow,
  onEditRow,
  onNotesChange,
  onNotesBlur,
  syncRowHeight,
  onPositionChange,
  isAdmin = false
}) => {
  const handlePositionChange = useCallback(
    (id: number, position: string) => {
      if (onPositionChange) {
        onPositionChange(id, position);
      } else {
        // Fallback: update via onRowsChange
        const updatedEmployees = employees.map(emp =>
          emp.id === id ? { ...emp, position } : emp
        );
        const indexes = [employees.findIndex(emp => emp.id === id)];
        onRowsChange(updatedEmployees, { indexes } as RowsChangeData<Employee>);
      }
    },
    [employees, onPositionChange, onRowsChange]
  );
  const columns = useMemo<Column<Employee>[]>(() => {
    const baseColumns: Column<Employee>[] = [
      {
        key: 'name',
        name: 'Name',
        editable: isAdmin,
        width: 180,
        minWidth: 150,
        renderCell: ({ row }: RenderCellProps<Employee>) => (
          <div
            onClick={e => {
              e.stopPropagation();
              onRowClick(row);
            }}
            style={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Click to view point tracking"
          >
            {row.name}
          </div>
        )
      },
      {
        key: 'department',
        name: 'Department',
        editable: isAdmin,
        width: 150,
        minWidth: 120
      },
      {
        key: 'position',
        name: 'Position',
        editable: false,
        width: 200,
        minWidth: 170,
        renderCell: ({ row }: RenderCellProps<Employee>) => (
          <PositionCell
            row={row}
            options={POSITION_OPTIONS}
            onPositionChange={handlePositionChange}
            disabled={!isAdmin}
          />
        )
      },
      {
        key: 'points',
        name: 'Points',
        editable: isAdmin,
        width: 100,
        minWidth: 80
      },
      {
        key: 'performance_score',
        name: 'Performance Score',
        editable: isAdmin,
        width: 180,
        minWidth: 150,
        renderCell: ({ row }: RenderCellProps<Employee>) =>
          row.performance_score !== null ? row.performance_score.toFixed(2) : ''
      },
      {
        key: 'notes',
        name: 'Notes',
        editable: false,
        minWidth: 300,
        renderCell: ({ row }: RenderCellProps<Employee>) => (
          <NotesCell
            row={row}
            onChange={onNotesChange}
            onBlur={onNotesBlur}
            syncRowHeight={syncRowHeight}
            disabled={!isAdmin}
          />
        )
      }
    ];

    // Only add actions column for admins
    if (isAdmin) {
      baseColumns.push({
        key: 'actions',
        name: 'Actions',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        resizable: false,
        renderCell: ({ row }: RenderCellProps<Employee>) => (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={e => {
                e.stopPropagation();
                onEditRow(row);
              }}
              className="edit-btn"
              title="Edit employee"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            {row.id > 0 && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDeleteRow(row.id);
                }}
                className="delete-btn"
                title="Archive employee"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            )}
          </div>
        )
      });
    }

    return baseColumns;
  }, [handlePositionChange, isAdmin, onDeleteRow, onEditRow, onNotesBlur, onNotesChange, onRowClick, syncRowHeight]);

  const gridHeight = useMemo(() => {
    return employees.reduce(
      (total, row) => total + rowHeightGetter(row),
      HEADER_ROW_HEIGHT
    );
  }, [employees, rowHeightGetter]);

  return (
    <div className="grid-container">
      <DataGrid
        columns={columns}
        rows={employees}
        onRowsChange={onRowsChange}
        defaultColumnOptions={{
          sortable: true,
          resizable: true
        }}
        className="rdg-light"
        style={{
          width: '100%',
          height: gridHeight
        }}
        rowHeight={rowHeightGetter}
        headerRowHeight={HEADER_ROW_HEIGHT}
      />
    </div>
  );
};

export default EmployeeGrid;

